import { Alert, Card, Tabs, Typography } from 'antd';
import FixedDashboard from '../components/common/FixedDashboard';
import FixedInvestmentsTab from '../components/common/FixedInvestmentsTab';
import FixedLedgerTab from '../components/common/FixedLedgerTab';
import FixedWithdrawalsTab from '../components/common/FixedWithdrawalsTab';
import DealerInvestmentsTab from '../components/dealer/DealerInvestmentsTab';
import DealerLedgerTab from '../components/dealer/DealerLedgerTab';
import DealerMyDashboard from '../components/dealer/DealerMyDashboard';
import DealerProfileTab from '../components/dealer/DealerProfileTab';
import DealerProfitTab from '../components/dealer/DealerProfitTab';
import DealerWithdrawalsTab from '../components/dealer/DealerWithdrawalsTab';
import { useDealer } from '../api/hooks';
import { useAuth } from '../context/AuthContext';
import { useTabHistory } from '../utils/useTabHistory';
import { useTranslation } from 'react-i18next';

/**
 * Read-only self-service view for dealer-role logins — the mirror of InvestorPortalPage. The write
 * buttons inside the shared tabs are hidden by each tab's own `hasRole('Admin')` check, and the API
 * rejects a dealer's write attempts regardless of what the UI shows.
 *
 * One deliberate simplification versus the investor portal: there is no separate Overview/Ledger
 * split here. DealerLedgerTab already carries both the summary cards and the full transaction
 * table, so splitting it into two tabs would only show the same numbers twice.
 */
export default function DealerPortalPage() {
  const { t } = useTranslation();
  const tabs = useTabHistory('overview');
  const { user } = useAuth();
  const dealerId = user?.dealerId ?? 0;
  const { data: dealer } = useDealer(dealerId);
  const isFixed = dealer?.dealType === 'FixedReturn';

  if (!dealerId) {
    return <Alert type="error" showIcon message="This account is not linked to a dealer record." />;
  }

  return (
    <>
      <Typography.Title level={3}>My Investments</Typography.Title>

      <Card>
        <Tabs
          activeKey={tabs.activeKey}
          onChange={tabs.onChange}
          items={[
            {
              key: 'overview',
              label: t('tabs.overview'),
              children: isFixed ? (
                <FixedDashboard partyType="Dealer" partyId={dealerId} />
              ) : (
                <DealerMyDashboard dealerId={dealerId} />
              ),
            },
            { key: 'profile', label: t('tabs.profile'), children: <DealerProfileTab dealerId={dealerId} /> },
            ...(isFixed
              ? [
                  {
                    key: 'investments',
                    label: t('tabs.investments'),
                    children: <FixedInvestmentsTab partyType="Dealer" partyId={dealerId} canWrite={false} />,
                  },
                  {
                    key: 'withdrawals',
                    label: t('tabs.withdrawals'),
                    children: <FixedWithdrawalsTab partyType="Dealer" partyId={dealerId} canWrite={false} />,
                  },
                ]
              : [
                  { key: 'investments', label: t('tabs.investments'), children: <DealerInvestmentsTab dealerId={dealerId} /> },
                  { key: 'profit', label: t('tabs.profit'), children: <DealerProfitTab dealerId={dealerId} /> },
                  { key: 'withdrawals', label: t('tabs.withdrawals'), children: <DealerWithdrawalsTab dealerId={dealerId} /> },
                ]),
            {
              key: 'ledger',
              label: t('tabs.ledger'),
              children: isFixed ? (
                <FixedLedgerTab partyType="Dealer" partyId={dealerId} />
              ) : (
                <DealerLedgerTab dealerId={dealerId} />
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
